import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Copy } from 'lucide-react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary capturó un error:', error, errorInfo);
  }

  handleCopy = () => {
    const { error, errorInfo } = this.state;
    const text = `Error: ${error?.message || error}\n\nStack:\n${error?.stack || ''}\n\nComponent Stack:\n${errorInfo?.componentStack || ''}`;
    navigator.clipboard?.writeText(text);
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, errorInfo } = this.state;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="error-boundary">
        <div className="max-w-2xl w-full bg-card border border-destructive/30 rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-3 bg-destructive/10 px-6 py-4 border-b border-destructive/20">
            <AlertTriangle className="h-6 w-6 text-destructive flex-shrink-0" />
            <div>
              <h1 className="text-lg font-semibold text-foreground">Ocurrió un error inesperado</h1>
              <p className="text-sm text-muted-foreground">La aplicación encontró un problema al mostrar esta pantalla.</p>
            </div>
          </div>

          <div className="px-6 py-4 space-y-3">
            <div>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Mensaje</span>
              <p className="mt-1 font-mono text-sm text-destructive break-words" data-testid="error-message">
                {error?.message || String(error)}
              </p>
            </div>

            {(error?.stack || errorInfo?.componentStack) && (
              <details className="rounded border border-border bg-muted/40">
                <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">
                  Ver detalle técnico (stack trace)
                </summary>
                <pre className="max-h-64 overflow-auto px-3 pb-3 text-xs whitespace-pre-wrap text-muted-foreground">
{error?.stack}
{errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>

          <div className="flex flex-wrap gap-2 px-6 py-4 border-t border-border">
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary inline-flex items-center gap-2 px-4 py-2 rounded text-sm bg-primary text-primary-foreground hover:opacity-90"
              data-testid="error-reload-btn"
            >
              <RefreshCw className="h-4 w-4" /> Recargar página
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm border border-border hover:bg-muted"
              data-testid="error-home-btn"
            >
              <Home className="h-4 w-4" /> Volver al inicio
            </button>
            <button
              onClick={this.handleCopy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm border border-border hover:bg-muted"
              data-testid="error-copy-btn"
            >
              <Copy className="h-4 w-4" /> Copiar detalle
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
